import { useState } from "react";
import { X, User, Phone, Mail, Briefcase, Globe2, Flag } from "lucide-react";

// Countries shown in the "Preferred Country" dropdown, with flag emoji to
// match the flags already used in the leads table.
const COUNTRIES = [
  { name: "Poland", flag: "🇵🇱" },
  { name: "Romania", flag: "🇷🇴" },
  { name: "Serbia", flag: "🇷🇸" },
  { name: "Lithuania", flag: "🇱🇹" },
  { name: "Hungary", flag: "🇭🇺" },
  { name: "Portugal", flag: "🇵🇹" },
  { name: "Montenegro", flag: "🇲🇪" },
  { name: "Slovakia", flag: "🇸🇰" },
  { name: "Czech Republic", flag: "🇨🇿" },
  { name: "Germany", flag: "🇩🇪" },
  { name: "Estonia", flag: "🇪🇪" },
  { name: "Latvia", flag: "🇱🇻" },
];

// The API sometimes bakes the flag emoji directly into the string
// (e.g. "Romania \uD83C\uDDF7\uD83C\uDDF4"), so we match by substring rather
// than exact equality, and always return our own clean canonical name.
function normalizeCountry(value) {
  if (!value) return value;
  const cleaned = value.trim();
  const match = COUNTRIES.find((c) =>
    cleaned.toLowerCase().includes(c.name.toLowerCase())
  );
  return match ? match.name : cleaned; // fall back to raw value if truly unknown
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-500">
        <Icon size={14} className="text-slate-400" />
        {label}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 " +
  "placeholder:text-slate-400 focus:border-blue-400 focus:outline-none " +
  "focus:ring-2 focus:ring-blue-100 transition-colors";

export default function EditLeadModal({
  lead,
  onCancel = () => {},
  onSave = () => {},
}) {
  const [form, setForm] = useState({
    ...lead,
    preferredCountry: normalizeCountry(lead.preferredCountry),
  });
  const [saving, setSaving] = useState(false);

  // True if the lead's country isn't in our known list at all (e.g. a typo,
  // or a country we haven't added yet) — rendered as a fallback option so
  // the select doesn't silently jump to the first item in the list.
  const isUnknownCountry =
    form.preferredCountry &&
    !COUNTRIES.some((c) => c.name === form.preferredCountry);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Edit lead</h2>
          <p className="text-xs text-slate-400">Lead #{form.id}</p>
        </div>
        <button
          onClick={onCancel}
          aria-label="Close"
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={18} />
        </button>
      </div>

      {/* Fields */}
      <div className="flex flex-col gap-4 px-5 py-4">
        <Field label="Full name" icon={User}>
          <input
            className={inputClass}
            value={form.fullName}
            onChange={update("fullName")}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone" icon={Phone}>
            <input
              className={inputClass}
              value={form.phoneNumber}
              onChange={update("phoneNumber")}
            />
          </Field>
          <Field label="Nationality" icon={Flag}>
            <input
              className={inputClass}
              value={form.nationality}
              onChange={update("nationality")}
            />
          </Field>
        </div>

        <Field label="Email" icon={Mail}>
          <input
            type="email"
            className={inputClass}
            value={form.email}
            onChange={update("email")}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Service required" icon={Briefcase}>
            <input
              className={inputClass}
              value={form.serviceRequired}
              onChange={update("serviceRequired")}
            />
          </Field>
          <Field label="Preferred country" icon={Globe2}>
            <select
              className={inputClass}
              value={form.preferredCountry}
              onChange={update("preferredCountry")}
            >
              {isUnknownCountry && (
                <option value={form.preferredCountry}>
                  {form.preferredCountry} (unrecognized)
                </option>
              )}
              {COUNTRIES.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.flag} {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </div>
  );
}